import React, { ReactNode } from 'react';
import { Error as SherpaError, Info as SherpaInfo, Success as SherpaSuccess } from '@bshgroup/component-icons';
import {
  HintContent as SherpaHintContent,
  HintContentDescription as SherpaHintContentDescription,
  HintContentIcon as SherpaHintContentIcon,
  Hint as SherpaHint,
} from '@bshgroup/component-hint';

export type HintLevel = 'error' | 'info' | 'highlight' | 'success';

export interface HintProps {
  /**
   * Hint text
   */
  description: string | ReactNode;
  /**
   * Hint display level
   */
  level?: HintLevel;
  /**
   * Custom icon
   */
  icon?: any;
}

const styles: { [key in HintLevel]: { color?: any; hint: any; icon?: any } } = {
  error: {
    color: { color: 'rgba(190, 0, 4, 1)' },
    hint: { backgroundColor: 'rgba(190, 0, 4, 0.1)', marginTop: '-0.5rem' },
    icon: SherpaError,
  },
  info: {
    hint: { backgroundColor: 'rgb(209, 228, 255)' },
    icon: SherpaInfo,
  },
  highlight: {
    hint: { backgroundColor: 'rgb(209, 228, 255)' },
  },
  success: {
    hint: { backgroundColor: 'rgba(184, 239, 201)' },
    icon: SherpaSuccess,
  },
};

/**
 * The `Hint` component is used to display a hint with markup defined by the hint level.
 */
export default function Hint({
  description,
  level,
  icon,
  ...props
}: HintProps & Omit<Partial<React.HTMLAttributes<HTMLDivElement>>, keyof HintProps>) {
  return (
    <SherpaHint
      css={level ? styles[level].hint : null}
      content={config => (
        <SherpaHintContent
          {...config}
          icon={config =>
            icon || (level && styles[level].icon) ? (
              <SherpaHintContentIcon
                as={icon || (level && styles[level].icon)}
                css={level ? styles[level].color : null}
                {...config}
              />
            ) : (
              (undefined as any)
            )
          }
          description={config => (
            <SherpaHintContentDescription {...config} css={level ? styles[level].color : null}>
              {description}
            </SherpaHintContentDescription>
          )}
        ></SherpaHintContent>
      )}
      {...(props as any)}
    />
  );
}
